import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, LoadingSpinner, DataGrid } from '../layout';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';
import Swal from 'sweetalert2';
import {
    FaDownload,
    FaRedo,
    FaFilter,
    FaCheckCircle,
    FaArchive,
    FaEye,
    FaTrash
} from 'react-icons/fa';

const StatusBadge = ({ status }) => {
    const styles = {
        attivo: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
        revisionato: 'bg-gray-100 text-gray-600 border border-gray-300'
    };
    const labels = {
        attivo: 'Attivo',
        revisionato: 'Revisionato'
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles[status] || styles.attivo}`}>
            {status === 'attivo' ? <FaCheckCircle className="text-xs" /> : <FaArchive className="text-xs" />}
            {labels[status] || status}
        </span>
    );
};

const StoricoCapitolati = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [capitolati, setCapitolati] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');

    const statusFilters = [
        { key: 'all', label: 'Tutti', icon: <FaFilter />, color: 'gray' },
        { key: 'attivo', label: 'Attivo', icon: <FaCheckCircle />, color: 'emerald' },
        { key: 'revisionato', label: 'Revisionato', icon: <FaArchive />, color: 'gray' },
    ];

    useEffect(() => {
        setDocumentTitle('Storico Capitolati');
    }, []);

    useEffect(() => {
        if (user) fetchCapitolati();
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredCapitolati = statusFilter === 'all'
        ? capitolati
        : capitolati.filter(c => c.status === statusFilter);

    const handleFilterChange = setStatusFilter;

    const fetchCapitolati = async () => {
        setIsLoading(true);
        try {
            const isAdmin = user?.role === 'admin';
            const creatorId = user?.userType === 'agent'
                ? (user?.agent_data?.Salesperson || user?.id?.toString())
                : (isAdmin ? null : user?.id?.toString());

            const response = await api.getCapitolati(creatorId, isAdmin);
            if (response.status === 'success') {
                setCapitolati(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching capitolati:', error);
            Swal.fire('Errore', 'Impossibile caricare lo storico capitolati', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadPdf = (id) => {
        window.open(api.getCapitolatoPdfUrl(id), '_blank');
    };

    const handleCreateRevision = async (capitolato) => {
        const confirm = await Swal.fire({
            title: 'Crea Revisione',
            html: `Vuoi creare una <strong>REV ${(capitolato.revision || 1) + 1}</strong> del capitolato <strong>${capitolato.capitolato_number}</strong>?<br><br>Il capitolato corrente verrà marcato come "Revisionato".`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Crea Revisione',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#cd212a'
        });

        if (!confirm.isConfirmed) return;

        try {
            const response = await api.getCapitolatoById(capitolato.id);
            if (response.status === 'success') {
                navigate('/capitolato/form', {
                    state: { revisionData: response.data }
                });
            }
        } catch (error) {
            console.error('Error loading capitolato for revision:', error);
            Swal.fire('Errore', 'Impossibile caricare i dati del capitolato', 'error');
        }
    };

    const isAdmin = user?.role === 'admin';

    const handleDelete = async (cap) => {
        const confirm = await Swal.fire({
            title: 'Elimina Capitolato',
            html: `Sei sicuro di voler eliminare il capitolato <strong>${cap.capitolato_number}</strong>?<br>Questa azione è irreversibile.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Elimina',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#ef4444'
        });
        if (!confirm.isConfirmed) return;
        try {
            await api.deleteCapitolato(cap.id);
            setCapitolati(prev => prev.filter(c => c.id !== cap.id));
            Swal.fire('Eliminato', 'Capitolato eliminato con successo', 'success');
        } catch (error) {
            console.error('Error deleting capitolato:', error);
            Swal.fire('Errore', 'Impossibile eliminare il capitolato', 'error');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const columns = [
        {
            key: 'capitolato_number',
            header: 'N. Capitolato',
            cellClassName: 'px-4 font-medium text-gray-900',
            sortValue: (cap) => cap.capitolato_number,
            render: (cap) => cap.capitolato_number
        },
        {
            key: 'rif_cantiere',
            header: 'Rif. Cantiere',
            cellClassName: 'px-4 text-gray-700',
            sortValue: (cap) => cap.rif_cantiere,
            render: (cap) => cap.rif_cantiere || 'N/A'
        },
        {
            key: 'modello_ninfa',
            header: 'Modello',
            cellClassName: 'px-4 text-gray-700',
            sortValue: (cap) => cap.modello_ninfa,
            render: (cap) => (
                <>
                    {cap.modello_ninfa || 'N/A'}
                    {cap.config_count && parseInt(cap.config_count) > 1 && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            {cap.config_count} config.
                        </span>
                    )}
                </>
            )
        },
        {
            key: 'nominativi',
            header: 'Cliente',
            cellClassName: 'px-4 text-gray-700',
            sortValue: (cap) => cap.nominativi,
            render: (cap) => cap.nominativi || 'N/A'
        },
        {
            key: 'revision',
            header: 'REV',
            headerClassName: 'px-4 min-w-[90px]',
            sortValue: (cap) => Number(cap.revision) || 0,
            render: (cap) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 whitespace-nowrap">
                    REV {cap.revision}
                </span>
            )
        },
        {
            key: 'created_at',
            header: 'Data',
            cellClassName: 'px-4 text-gray-700',
            sortValue: (cap) => (cap.created_at ? new Date(cap.created_at).getTime() : null),
            render: (cap) => formatDate(cap.created_at)
        },
        {
            key: 'status',
            header: 'Stato',
            sortValue: (cap) => cap.status,
            render: (cap) => <StatusBadge status={cap.status} />
        },
        {
            key: 'actions',
            header: 'Azioni',
            render: (cap) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleDownloadPdf(cap.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs font-medium transition-colors"
                        title="Scarica PDF"
                    >
                        <FaDownload className="text-xs" />
                        PDF
                    </button>
                    {cap.status === 'attivo' && (
                        <button
                            onClick={() => handleCreateRevision(cap)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 text-xs font-medium transition-colors"
                            title="Crea Revisione"
                        >
                            <FaRedo className="text-xs" />
                            Revisione
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            onClick={() => handleDelete(cap)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-xs font-medium transition-colors"
                            title="Elimina"
                        >
                            <FaTrash className="text-xs" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header title="Storico Capitolati" backTo="/dashboard" />
                <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[60vh]">
                    <LoadingSpinner />
                    <p className="mt-4 text-gray-600">Caricamento storico capitolati...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header title="Storico Capitolati" backTo="/dashboard" />

            <div className="container mx-auto px-4 py-8">
                {/* Status Filter Pills */}
                <div className="mb-6 flex flex-wrap gap-2">
                    {statusFilters.map(filter => (
                        <button
                            key={filter.key}
                            onClick={() => handleFilterChange(filter.key)}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                statusFilter === filter.key
                                    ? 'bg-red-600 text-white shadow-lg'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {filter.icon}
                            {filter.label}
                            {filter.key === 'all' && (
                                <span className="ml-1 bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs">
                                    {capitolati.length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {filteredCapitolati.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center">
                        <FaArchive className="mx-auto text-4xl text-gray-300 mb-4" />
                        <p className="text-gray-600 text-lg">
                            {statusFilter === 'all'
                                ? 'Nessun capitolato emesso.'
                                : `Nessun capitolato con stato "${statusFilters.find(f => f.key === statusFilter)?.label}".`}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <DataGrid
                            columns={columns}
                            rows={filteredCapitolati}
                            rowKey="id"
                            searchKeys={['capitolato_number', 'rif_cantiere', 'modello_ninfa', 'nominativi', 'status', 'revision']}
                            searchPlaceholder="Cerca capitolato, cantiere, cliente..."
                            initialSort={{ key: 'created_at', dir: 'desc' }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default StoricoCapitolati;
